import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { apiService } from '../../services/apiService';

export default function Bridge() {
  const [status, setStatus] = useState('initializing'); // initializing, waiting, uploading, done, error
  const [errorMessage, setErrorMessage] = useState(null);
  // Self-service handoff, used when the task pane never acts on our messageParent (see below).
  const [handoff, setHandoff] = useState(null); // { url, aiOrderNumber }
  const [stalled, setStalled] = useState(false);
  const [handedOff, setHandedOff] = useState(false);
  const handlerRegistered = useRef(false);
  const uploadStartedRef = useRef(false);
  const stallTimerRef = useRef(null);
  const retryTimerRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(stallTimerRef.current);
    clearTimeout(retryTimerRef.current);
  }, []);

  // The CSR clicked "Continue in browser". The anchor's own default action opens the review
  // tab (user-initiated, so it isn't popup-blocked); this only dismisses the dialog behind it.
  //
  // Reported by Tonya 2026-08-06: the window stayed open after clicking, and "clicking it again
  // makes it reprocess the order" — re-opening the review URL re-runs processing for a PO that
  // is already uploaded. So closing here fixes both halves: the window goes away, and the link
  // can't be clicked a second time.
  //
  // The close is deferred so it cannot cancel the navigation still being handed to the system
  // browser, and is best-effort: a host that refuses window.close() falls through to the
  // "You can close this window" state rather than leaving a dead-looking button on screen.
  function handleContinueInBrowser() {
    setHandedOff(true);
    setTimeout(() => {
      try {
        window.close();
      } catch {
        /* Host refused — the on-screen message covers it. */
      }
    }, 500);
  }

  useEffect(() => {
    async function init() {
      // Check auth first — redirect to AAD login with post_login_redirect_uri if needed
      try {
        const authRes = await fetch('/.auth/me');
        const authData = await authRes.json();
        if (!authData.clientPrincipal) {
          window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(window.location.href)}`;
          return;
        }
      } catch {
        // In dev/local, /.auth/me may not exist — continue without auth
      }

      // Wait for Office.js to be ready — in dialog context, host may be null
      if (typeof Office !== 'undefined' && Office.onReady) {
        try {
          await Office.onReady();
        } catch {
          setStatus('error');
          setErrorMessage('Failed to initialize Office.js');
          return;
        }
      }

      if (!Office?.context?.ui) {
        setStatus('error');
        setErrorMessage('This page must be opened from the Outlook add-in.');
        return;
      }

      if (handlerRegistered.current) return;
      handlerRegistered.current = true;

      // Register handler for messages from the parent (task pane)
      Office.context.ui.addHandlerAsync(
        Office.EventType.DialogParentMessageReceived,
        async (arg) => {
          // Upload exactly once per dialog. Office can deliver the parent message
          // more than once on some Outlook clients, which would otherwise mint
          // duplicate correlationIds / AI order numbers.
          if (uploadStartedRef.current) return;
          uploadStartedRef.current = true;
          try {
            const fileData = JSON.parse(arg.message);
            setStatus('uploading');

            // Get user info from SWA auth
            let userId = 'unknown';
            let userEmail = 'unknown';
            try {
              const authRes = await fetch('/.auth/me', { credentials: 'include' });
              const authData = await authRes.json();
              if (authData.clientPrincipal) {
                userId = authData.clientPrincipal.userId;
                userEmail = authData.clientPrincipal.userDetails;
              }
            } catch {
              // Continue with unknown user — upload will still work
            }

            // Upload to the existing API endpoint
            const uploadResult = await apiService.uploadDocument(
              fileData.content,
              fileData.fileName,
              fileData.mimeType,
              userId,
              userEmail
            );

            const correlationId = uploadResult.correlationId;

            // Store upload result in localStorage for the main app to pick up
            // The browser tab and this dialog share the same origin → same localStorage
            localStorage.setItem(`po-outlook-${correlationId}`, JSON.stringify({
              correlationId: correlationId,
              aiOrderNumber: uploadResult.aiOrderNumber || null,
              sasUrl: uploadResult.sasUrl,
              fileName: fileData.fileName,
              mimeType: fileData.mimeType,
              officeViewerUrl: uploadResult.officeViewerUrl || null,
              canUseOfficeViewer: uploadResult.canUseOfficeViewer || false,
              asyncProcessing: uploadResult.asyncProcessing || false,
              timestamp: Date.now(),
            }));

            setStatus('done');

            // Send full upload data back to the task pane (system browser can't share localStorage)
            Office.context.ui.messageParent(JSON.stringify({
              correlationId,
              aiOrderNumber: uploadResult.aiOrderNumber || null,
              sasUrl: uploadResult.sasUrl,
              fileName: fileData.fileName,
              mimeType: fileData.mimeType,
              officeViewerUrl: uploadResult.officeViewerUrl || null,
              canUseOfficeViewer: uploadResult.canUseOfficeViewer || false,
              asyncProcessing: uploadResult.asyncProcessing || false,
            }));

            // This dialog cannot finish the job itself: the TASK PANE closes it and opens the
            // browser tab. When Outlook drops that DialogMessageReceived the dialog sits on
            // "Closing..." forever, no tab opens, extraction never starts, and the CSR re-sends the
            // mail -- burning a second AI order number for the same PO. Seen in prod 2026-07-30:
            // AI18R stranded at 10:37 with no extraction, AI18S was the 10:40 retry.
            // We can't make Office deliver the message, so make the failure recoverable: after a
            // grace period, offer the same URL the task pane would have opened. Mirrors the params
            // built in TaskPane.jsx -- keep the two in sync.
            const params = new URLSearchParams({ from: 'outlook', cid: correlationId });
            if (uploadResult.aiOrderNumber) params.set('aiOrderNumber', uploadResult.aiOrderNumber);
            if (uploadResult.sasUrl) params.set('sasUrl', uploadResult.sasUrl);
            if (fileData.fileName) params.set('fileName', fileData.fileName);
            if (fileData.mimeType) params.set('mimeType', fileData.mimeType);
            if (uploadResult.officeViewerUrl) params.set('officeViewerUrl', uploadResult.officeViewerUrl);
            if (uploadResult.canUseOfficeViewer) params.set('canUseOfficeViewer', '1');
            if (uploadResult.asyncProcessing) params.set('asyncProcessing', '1');
            setHandoff({
              url: `${window.location.origin}/?${params.toString()}`,
              aiOrderNumber: uploadResult.aiOrderNumber || null,
            });
            // Still here after a moment means the task pane never acted on the message above —
            // Outlook dropped it. Send it once more before giving up: the task pane guards
            // against duplicate delivery (it closes the dialog first, then handles the payload
            // exactly once), so a repeat is safe and usually turns a stall back into a normal
            // close-and-open-tab. Only if THAT is also dropped do we show the manual handoff.
            retryTimerRef.current = setTimeout(() => {
              try {
                Office.context.ui.messageParent(JSON.stringify({
                  correlationId,
                  aiOrderNumber: uploadResult.aiOrderNumber || null,
                  sasUrl: uploadResult.sasUrl,
                  fileName: fileData.fileName,
                  mimeType: fileData.mimeType,
                  officeViewerUrl: uploadResult.officeViewerUrl || null,
                  canUseOfficeViewer: uploadResult.canUseOfficeViewer || false,
                  asyncProcessing: uploadResult.asyncProcessing || false,
                }));
              } catch {
                /* Dialog already closing — nothing to do. */
              }
            }, 1200);

            // If the task pane does its job the dialog is already gone and this never fires.
            stallTimerRef.current = setTimeout(() => setStalled(true), 3000);
          } catch (err) {
            setStatus('error');
            setErrorMessage(err.message || 'Upload failed');
            Office.context.ui.messageParent(`error:${err.message || 'Upload failed'}`);
          }
        }
      );

      // Tell the task pane we're ready to receive the file
      setStatus('waiting');
      Office.context.ui.messageParent('ready');
    }

    init();
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="text-center">
        {status === 'initializing' && (
          <>
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Initializing...</p>
          </>
        )}
        {status === 'waiting' && (
          <>
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Waiting for attachment...</p>
          </>
        )}
        {status === 'uploading' && (
          <>
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-3" />
            <p className="text-white font-medium text-sm mb-1">Uploading to Order Processor</p>
            <p className="text-slate-400 text-xs">This window will close automatically.</p>
          </>
        )}
        {status === 'done' && (
          <>
            <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="text-white font-medium text-sm">Upload complete</p>
            {stalled && handoff ? (
              handedOff ? (
                /* Handed off. The link is deliberately gone: clicking it again would re-open the
                   review URL and reprocess an order that is already uploaded. */
                <>
                  <p className="text-slate-400 text-xs mt-1 mb-2">
                    Opening in your browser...
                  </p>
                  <p className="text-slate-500 text-[11px] leading-snug">
                    You can close this window.
                    {handoff.aiOrderNumber
                      ? ` Order ${handoff.aiOrderNumber} is already uploaded — don't send the email again.`
                      : " This PO is already uploaded — don't send the email again."}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-slate-400 text-xs mt-1 mb-3">
                    This window didn't close on its own.
                  </p>
                  {/* A plain anchor, so the click is user-initiated and won't be popup-blocked.
                      onClick only dismisses this dialog — it does not open the tab. */}
                  <a
                    href={handoff.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={handleContinueInBrowser}
                    className="inline-block bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-xs rounded-lg px-4 py-2 transition-colors"
                  >
                    Continue in browser &rarr;
                  </a>
                  <p className="text-slate-500 text-[11px] mt-3 leading-snug">
                    {handoff.aiOrderNumber
                      ? `Order ${handoff.aiOrderNumber} is already uploaded — use this link rather than sending the email again.`
                      : 'This PO is already uploaded — use this link rather than sending the email again.'}
                  </p>
                </>
              )
            ) : (
              <p className="text-slate-400 text-xs">Closing...</p>
            )}
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-red-400 font-medium text-sm mb-1">Upload Failed</p>
            <p className="text-slate-500 text-xs">{errorMessage}</p>
          </>
        )}
      </div>
    </div>
  );
}
